import { create } from "zustand";
import { UserProps, UserStatus } from "../../pages/admin-panel/users/types";
import { Customer, ObjectId, Tag, UpdateFn, Updater } from "../../types";
import { DEBUG } from "../../api";
import { DUMMY_DATA } from "../../pages/admin-panel/users/data";
import useDashboardStore from "../dashboard-store";
import { Activity } from "../../components/activity/types";

interface State {
  users: Array<UserProps>;
  tags: Array<Tag>;
  topTags: Array<ObjectId>;
  customers: Array<Customer>;
}

interface Action {
  setter: <T extends keyof State>(key: T, updater: Updater<State[T]>) => void;

  setUsers: UpdateFn<Array<UserProps>>;
  addUser: (user: UserProps) => void;
  editUser: (user: UserProps) => void;
  deleteUser: (id: string) => void;
  updateUserStatus: (id: string, status: UserStatus) => void;
  getUserById: (id: string) => UserProps | null;

  setTags: UpdateFn<Array<Tag>>;
  addTag: (tag: Tag) => void;
  getTagById: (id: string) => Tag | null;

  setTopTags: UpdateFn<Array<ObjectId>>;

  setCustomers: UpdateFn<Array<Customer>>;
  addCustomer: (customer: Customer) => void;
  editCustomer: (customer: Customer) => void;
  deleteCustomer: (id: string, activity: Activity) => void;
  getCustomerById: (id: string) => Customer | null;
}

const useGeneralStore = create<State & Action>((set, get) => ({
  users: DEBUG ? DUMMY_DATA : [],
  tags: [],
  topTags: [],
  customers: [],

  setter: <T extends keyof State>(key: T, updater: Updater<State[T]>): void => {
    if (typeof updater === "function") {
      set((state) => ({ [key]: updater(state[key]) }));
    } else {
      set({ [key]: updater });
    }
  },

  setUsers: (updater: Updater<Array<UserProps>>): void => {
    get().setter("users", updater);
  },
  addUser: (user: UserProps): void => {
    get().setUsers((prev) => [...prev, user]);
  },
  editUser: (user: UserProps): void => {
    get().setUsers((prev) => {
      for (let i = 0; i < prev.length; i++) {
        if (prev[i]._id.$oid !== user._id.$oid) continue;
        prev[i] = user;
        break;
      }
      return [...prev];
    });
  },
  deleteUser: (id) => {
    get().setUsers((prev) => {
      prev = prev.filter((u) => u._id.$oid !== id);
      return [...prev];
    });
  },
  updateUserStatus: (id, status) => {
    get().setUsers((prev) => {
      for (let i = 0; i < prev.length; i++) {
        if (prev[i]._id.$oid !== id) continue;
        prev[i] = { ...prev[i], status };
        break;
      }
      return [...prev];
    });
  },
  getUserById: (id) => {
    let res = get().users.filter((u) => u._id.$oid === id);
    return res.length ? res[0] : null;
  },

  setTags: (updater: Updater<Array<Tag>>): void => {
    get().setter("tags", updater);
  },
  addTag: (tag: Tag): void => {
    get().setTags((prev) => [tag, ...prev]);
  },
  getTagById: (id: string): Tag | null => {
    const f = get().tags.filter((t) => t._id.$oid === id);
    return f.length ? f[0] : null;
  },

  setTopTags: (updater) => {
    get().setter("topTags", updater);
  },

  setCustomers: (updater: Updater<Array<Customer>>): void => {
    get().setter("customers", updater);
  },
  addCustomer: (customer: Customer): void => {
    get().setCustomers((prev) => [customer, ...prev]);

    useDashboardStore.getState().activitiesAdd(customer.activities[0]);
  },
  editCustomer: (customer: Customer): void => {
    get().setCustomers((prev) => {
      for (let i = 0; i < prev.length; i++) {
        if (prev[i]._id.$oid !== customer._id.$oid) continue;
        prev[i] = customer;
        break;
      }
      return [...prev];
    });

    useDashboardStore.getState().activitiesAdd(customer.activities[0]);
  },
  deleteCustomer: (id: string, activity: Activity): void => {
    get().setCustomers((prev) => {
      prev = prev.filter((c) => c._id.$oid !== id);
      return [...prev];
    });

    useDashboardStore.getState().activitiesAdd(activity);
  },
  getCustomerById: (id: string): Customer | null => {
    const res = get().customers.filter((c) => c._id.$oid === id);
    return res.length ? res[0] : null;
  },
}));

export default useGeneralStore;
